import React, { useState, useEffect } from 'react';
import {
  Plus,
  Edit,
  Trash2,
  Eye,
  EyeOff,
  Check,
  X,
  Shield,
  Users,
  Settings,
  Package,
  Factory,
  Award,
  ShoppingCart,
  UserCheck,
  ShoppingBag,
  Layers,
  BarChart3,
  DollarSign
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import apiService from '../services/api';

interface Module {
  id: number;
  name: string;
  displayName: string;
  description: string;
  route: string;
  icon: string;
  category: string;
}

interface RolePermission {
  id: number;
  moduleId: number;
  canView: boolean;
  canCreate: boolean;
  canUpdate: boolean;
  canDelete: boolean;
  module: Module;
}

interface Role {
  id: number;
  name: string;
  description: string;
  isActive: boolean;
  createdBy: number;
  permissions: RolePermission[];
  creator: {
    id: number;
    name: string;
    email: string;
  };
}

const RoleManagement: React.FC = () => {
  const { t } = useTranslation();
  const [roles, setRoles] = useState<Role[]>([]);
  const [modules, setModules] = useState<Module[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingRole, setEditingRole] = useState<Role | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    permissions: [] as { moduleId: number; canView: boolean; canCreate: boolean; canUpdate: boolean; canDelete: boolean }[]
  });

  const iconMap: Record<string, any> = {
    'chip-management': Package,
    'company-management': Shield,
    'gps-management': Settings,
    'yarns': Package,
    'machines': Factory,
    'employees': Users,
    'qualities': Award,
    'purchases': ShoppingCart,
    'clients': UserCheck,
    'sales': ShoppingBag,
    'beams': Layers,
    'production': BarChart3,
    'salary': DollarSign,
  };

  useEffect(() => {
    fetchRoles();
    fetchModules();
  }, []);

  const fetchRoles = async () => {
    try {
      const data = await apiService.get('/roles');
      if (data.success) {
        setRoles(data.data);
      }
    } catch (error) {
      console.error('Error fetching roles:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchModules = async () => {
    try {
      const data = await apiService.get('/roles/modules');
      if (data.success) {
        setModules(data.data);
      }
    } catch (error) {
      console.error('Error fetching modules:', error);
    }
  };

  const handleCreateRole = () => {
    setEditingRole(null);
    setFormData({
      name: '',
      description: '',
      permissions: modules.map(module => ({
        moduleId: module.id,
        canView: false,
        canCreate: false,
        canUpdate: false,
        canDelete: false
      }))
    });
    setShowModal(true);
  };

  const handleEditRole = (role: Role) => {
    setEditingRole(role);
    setFormData({
      name: role.name,
      description: role.description || '',
      permissions: modules.map(module => {
        const existingPermission = role.permissions.find(p => p.moduleId === module.id);
        return {
          moduleId: module.id,
          canView: existingPermission?.canView || false,
          canCreate: existingPermission?.canCreate || false,
          canUpdate: existingPermission?.canUpdate || false,
          canDelete: existingPermission?.canDelete || false
        };
      })
    });
    setShowModal(true);
  };

  const handleSaveRole = async () => {
    try {
      const url = editingRole ? `/roles/${editingRole.id}` : '/roles';
      
      let data;
      if (editingRole) {
        data = await apiService.put(url, formData);
      } else {
        data = await apiService.post(url, formData);
      }

      if (data.success) {
        setShowModal(false);
        fetchRoles();
      } else {
        alert(data.message || 'Failed to save role');
      }
    } catch (error) {
      console.error('Error saving role:', error);
      alert('Failed to save role');
    }
  };

  const handleDeleteRole = async (roleId: number) => {
    if (!window.confirm('Are you sure you want to delete this role?')) return;

    try {
      const data = await apiService.delete(`/roles/${roleId}`);
      if (data.success) {
        fetchRoles();
      } else {
        alert(data.message || 'Failed to delete role');
      }
    } catch (error) {
      console.error('Error deleting role:', error);
      alert('Failed to delete role');
    }
  };

  const togglePermission = (moduleId: number, permission: string) => {
    setFormData(prev => ({
      ...prev,
      permissions: prev.permissions.map(p =>
        p.moduleId === moduleId
          ? { ...p, [permission]: !p[permission as keyof typeof p] }
          : p
      )
    }));
  };

  const toggleAllPermissions = (moduleId: number, value: boolean) => {
    setFormData(prev => ({
      ...prev,
      permissions: prev.permissions.map(p =>
        p.moduleId === moduleId
          ? {
            ...p,
            canView: value,
            canCreate: value,
            canUpdate: value,
            canDelete: value
          }
          : p
      )
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Floating Create Role button */}
      <div className="fixed top-6 right-6 z-[1002]">
        <button
          type="button"
          onClick={handleCreateRole}
          className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-blue-600 text-white shadow-xl hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200 hover:scale-105"
          title="Create Role"
        >
          <Plus className="h-6 w-6" />
        </button>
      </div>

      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900">Roles</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Role Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Description
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Permissions
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created By
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {roles.map((role) => (
                <tr key={role.id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{role.name}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">{role.description || '-'}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">
                      {role.permissions.filter(p => p.canView).length} modules
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">{role.creator.name}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleEditRole(role)}
                        className="text-blue-600 hover:text-blue-900"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteRole(role.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Role Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  {editingRole ? 'Edit Role' : 'Create Role'}
                </h3>
                <button
                  onClick={() => setShowModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Role Name</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter role name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    rows={3}
                    placeholder="Enter role description"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">Permissions</label>
                  <div className="space-y-4 max-h-96 overflow-y-auto">
                    {modules.map((module) => {
                      const permission = formData.permissions.find(p => p.moduleId === module.id);
                      const IconComponent = iconMap[module.name] || Settings;

                      return (
                        <div key={module.id} className="border border-gray-200 rounded-lg p-4">
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center space-x-3">
                              <IconComponent className="h-5 w-5 text-gray-500" />
                              <div>
                                <h4 className="text-sm font-medium text-gray-900">{module.displayName}</h4>
                                <p className="text-xs text-gray-500">{module.description}</p>
                              </div>
                            </div>
                            <div className="flex space-x-2">
                              <button
                                onClick={() => toggleAllPermissions(module.id, true)}
                                className="text-xs text-blue-600 hover:text-blue-800"
                              >
                                All
                              </button>
                              <button
                                onClick={() => toggleAllPermissions(module.id, false)}
                                className="text-xs text-gray-600 hover:text-gray-800"
                              >
                                None
                              </button>
                            </div>
                          </div>

                          <div className="grid grid-cols-4 gap-4">
                            {['canView', 'canCreate', 'canUpdate', 'canDelete'].map((perm) => (
                              <label key={perm} className="flex items-center space-x-2">
                                <input
                                  type="checkbox"
                                  checked={Boolean(permission?.[perm as keyof typeof permission])}
                                  onChange={() => togglePermission(module.id, perm)}
                                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                />
                                <span className="text-xs text-gray-700 capitalize">
                                  {perm.replace('can', '')}
                                </span>
                              </label>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveRole}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
                >
                  {editingRole ? 'Update Role' : 'Create Role'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RoleManagement;
